import React from 'react';
import { X, Calendar, Building, Cpu, DollarSign, Plus, Trash2, Layers } from 'lucide-react';

interface PricingTierDisplay {
  min_input_tokens: number;
  max_input_tokens?: number | null;
  input_price: string;
  output_price: string;
  cache_read_price?: string;
  cache_write_price?: string;
}

interface ModelPricing {
  id: string;
  provider: string;
  model: string;
  input_price: string;
  output_price: string;
  cache_read_price?: string;
  cache_write_price?: string;
  pricing_strategy: 'flat_per_mtok' | 'tiered_per_request';
  tiers?: PricingTierDisplay[] | null;
  is_active: boolean;
  effective_date: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
  notes?: string;
}

interface PricingTierForm {
  min_input_tokens: number;
  max_input_tokens?: number | null;
  input_price: number;
  output_price: number;
  cache_read_price?: number;
  cache_write_price?: number;
}

interface ModelPricingCreateRequest {
  provider: string;
  model: string;
  input_price: number;
  output_price: number;
  cache_read_price?: number;
  cache_write_price?: number;
  pricing_strategy: 'flat_per_mtok' | 'tiered_per_request';
  tiers?: PricingTierForm[];
  is_active: boolean;
  notes?: string;
}

interface CreateModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: () => void;
  formData: ModelPricingCreateRequest;
  setFormData: React.Dispatch<React.SetStateAction<ModelPricingCreateRequest>>;
  providers: string[];
  error?: string | null;
}

interface UpdateModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: () => void;
  formData: ModelPricingCreateRequest;
  setFormData: React.Dispatch<React.SetStateAction<ModelPricingCreateRequest>>;
  selectedPricing: ModelPricing | null;
  error?: string | null;
}

interface DetailsModalProps {
  show: boolean;
  onClose: () => void;
  pricing: ModelPricing | null;
}

export const CreatePricingModal: React.FC<CreateModalProps> = ({
  show,
  onClose,
  onSubmit,
  formData,
  setFormData,
  providers,
  error
}) => {
  const [isNewProvider, setIsNewProvider] = React.useState(false);
  const [newProviderName, setNewProviderName] = React.useState('');
  const [tierValidationError, setTierValidationError] = React.useState<string | null>(null);

  if (!show) return null;

  const handleProviderChange = (value: string) => {
    if (value === 'new') {
      setIsNewProvider(true);
      setFormData(prev => ({ ...prev, provider: newProviderName || '' }));
    } else {
      setIsNewProvider(false);
      setNewProviderName('');
      setFormData(prev => ({ ...prev, provider: value }));
    }
  };

  const handleNewProviderChange = (value: string) => {
    setNewProviderName(value);
    setFormData(prev => ({ ...prev, provider: value }));
  };

  const addTier = () => {
    setFormData(prev => ({
      ...prev,
      tiers: [
        ...(prev.tiers || []),
        { min_input_tokens: 0, max_input_tokens: null, input_price: 0, output_price: 0 },
      ],
    }));
  };

  const removeTier = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tiers: (prev.tiers || []).filter((_, i) => i !== index),
    }));
  };

  const updateTier = (index: number, key: keyof PricingTierForm, value: number | null) => {
    setFormData(prev => {
      const tiers = [...(prev.tiers || [])];
      const tier = { ...(tiers[index] || {}) } as PricingTierForm;
      if (key === 'max_input_tokens') {
        tier.max_input_tokens = value === null ? null : Number(value);
      } else if (key === 'min_input_tokens') {
        tier.min_input_tokens = Number(value || 0);
      } else if (key === 'input_price') {
        tier.input_price = Number(value || 0);
      } else if (key === 'output_price') {
        tier.output_price = Number(value || 0);
      } else if (key === 'cache_read_price') {
        tier.cache_read_price = value === null ? undefined : Number(value || 0);
      } else if (key === 'cache_write_price') {
        tier.cache_write_price = value === null ? undefined : Number(value || 0);
      }
      tiers[index] = tier;
      // Keep tiers sorted by min_input_tokens
      tiers.sort((a, b) => a.min_input_tokens - b.min_input_tokens);
      return { ...prev, tiers };
    });
  };

  const validateTiers = (tiers: PricingTierForm[] | undefined) => {
    if (!tiers || tiers.length === 0) return 'At least one tier is required for Tiered strategy';
    const sorted = [...tiers].sort((a, b) => a.min_input_tokens - b.min_input_tokens);
    if (sorted[0].min_input_tokens !== 0) return 'First tier must start at 0';
    for (let i = 0; i < sorted.length; i++) {
      const t = sorted[i];
      if (t.max_input_tokens != null && t.max_input_tokens <= t.min_input_tokens) {
        return 'Tier max must be greater than min when provided';
      }
      if (i < sorted.length - 1) {
        const next = sorted[i + 1];
        if (t.max_input_tokens == null) return 'Only last tier can have open-ended max';
        if (t.max_input_tokens !== next.min_input_tokens) return 'Tiers must be contiguous (prev.max == next.min)';
      }
    }
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-ui-text-primary">
              Add New Model Pricing
            </h3>
            <button
              onClick={onClose}
              className="text-ui-text-tertiary hover:text-ui-text-primary transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-red-600 dark:text-red-400 text-sm">⚠️</span>
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          )}
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                  Provider *
                </label>
                <select
                  value={isNewProvider ? "new" : formData.provider}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  required
                >
                  <option value="">Select Provider</option>
                  {providers.map(provider => (
                    <option key={provider} value={provider}>{provider}</option>
                  ))}
                  <option value="new">Other (New)</option>
                </select>
                {isNewProvider && (
                  <input
                    type="text"
                    value={newProviderName}
                    placeholder="Enter new provider name"
                    onChange={(e) => handleNewProviderChange(e.target.value)}
                    className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary mt-2"
                  />
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                  Model *
                </label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  placeholder="Enter model name"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                  Input Price (per 1M tokens) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.input_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, input_price: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                  Output Price (per 1M tokens) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.output_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, output_price: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                  Cache Read Price (per 1M tokens)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cache_read_price || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    cache_read_price: e.target.value ? parseFloat(e.target.value) : undefined 
                  }))}
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  placeholder="Optional"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                  Cache Write Price (per 1M tokens)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cache_write_price || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    cache_write_price: e.target.value ? parseFloat(e.target.value) : undefined 
                  }))}
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  placeholder="Optional"
                />
              </div>
            </div>
            
            {/* Pricing Strategy */}
            <div>
              <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                Pricing Strategy
              </label>
              <select
                value={formData.pricing_strategy}
                onChange={(e) => {
                  const val = e.target.value as 'flat_per_mtok' | 'tiered_per_request';
                  setFormData(prev => ({ ...prev, pricing_strategy: val }));
                }}
                className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
              >
                <option value="flat_per_mtok">Flat (per MTok)</option>
                <option value="tiered_per_request">Tiered (per request)</option>
              </select>
            </div>

            {/* Tier Editor */}
            {formData.pricing_strategy === 'tiered_per_request' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Layers size={16} className="text-ui-text-tertiary" />
                  <span className="text-sm font-medium text-ui-text-secondary">Tiers</span>
                </div>
                {tierValidationError && (
                  <div className="text-sm text-red-600 dark:text-red-400">{tierValidationError}</div>
                )}
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-ui-text-tertiary">
                        <th className="px-2 py-1">Min</th>
                        <th className="px-2 py-1">Max</th>
                        <th className="px-2 py-1">Input $/1M</th>
                        <th className="px-2 py-1">Output $/1M</th>
                        <th className="px-2 py-1">Cache R $/1M</th>
                        <th className="px-2 py-1">Cache W $/1M</th>
                        <th className="px-2 py-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(formData.tiers || []).map((t, i) => (
                        <tr key={i}>
                          <td className="px-2 py-1"><input type="number" min={0} value={t.min_input_tokens}
                            onChange={(e) => updateTier(i, 'min_input_tokens', Number(e.target.value))}
                            className="w-24 px-2 py-1 border border-ui-border-primary rounded" /></td>
                          <td className="px-2 py-1"><input type="number" min={0} value={t.max_input_tokens ?? ''}
                            placeholder="∞"
                            onChange={(e) => updateTier(i, 'max_input_tokens', e.target.value === '' ? null : Number(e.target.value))}
                            className="w-24 px-2 py-1 border border-ui-border-primary rounded" /></td>
                          <td className="px-2 py-1"><input type="number" min={0} step="0.01" value={t.input_price}
                            onChange={(e) => updateTier(i, 'input_price', Number(e.target.value))}
                            className="w-28 px-2 py-1 border border-ui-border-primary rounded" /></td>
                          <td className="px-2 py-1"><input type="number" min={0} step="0.01" value={t.output_price}
                            onChange={(e) => updateTier(i, 'output_price', Number(e.target.value))}
                            className="w-28 px-2 py-1 border border-ui-border-primary rounded" /></td>
                          <td className="px-2 py-1"><input type="number" min={0} step="0.01" value={t.cache_read_price ?? ''}
                            onChange={(e) => updateTier(i, 'cache_read_price', e.target.value === '' ? null : Number(e.target.value))}
                            placeholder="Optional"
                            className="w-28 px-2 py-1 border border-ui-border-primary rounded" /></td>
                          <td className="px-2 py-1"><input type="number" min={0} step="0.01" value={t.cache_write_price ?? ''}
                            onChange={(e) => updateTier(i, 'cache_write_price', e.target.value === '' ? null : Number(e.target.value))}
                            placeholder="Optional"
                            className="w-28 px-2 py-1 border border-ui-border-primary rounded" /></td>
                          <td className="px-2 py-1">
                            <button onClick={() => removeTier(i)} className="p-1 text-red-600 hover:text-red-800"><Trash2 size={16} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button onClick={addTier} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-ui-background-tertiary dark:bg-neutral-600 text-ui-text-secondary rounded hover:bg-ui-background-tertiary">
                  <Plus size={14} /> Add tier
                </button>
              </div>
            )}

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded border-ui-border-primary text-brand-primary focus:ring-brand-primary"
                />
                <span className="text-sm text-ui-text-secondary">Active</span>
              </label>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                rows={3}
                placeholder="Optional notes about this pricing"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-ui-border-primary">
            <button
              onClick={onClose}
              className="px-4 py-2 text-ui-text-secondary border border-ui-border-primary rounded-lg hover:bg-ui-background-tertiary transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (formData.pricing_strategy === 'tiered_per_request') {
                  const err = validateTiers(formData.tiers);
                  setTierValidationError(err);
                  if (err) return;
                }
                onSubmit();
              }}
              disabled={!formData.provider || !formData.model || formData.input_price < 0 || formData.output_price < 0}
              className="px-6 py-2 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primaryDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Pricing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const UpdatePricingModal: React.FC<UpdateModalProps> = ({
  show,
  onClose,
  onSubmit,
  formData,
  setFormData,
  selectedPricing,
  error
}) => {
  const [tierValidationError, setTierValidationError] = React.useState<string | null>(null);
  if (!show || !selectedPricing) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-ui-text-primary">
              Update Pricing for {selectedPricing.provider}/{selectedPricing.model}
            </h3>
            <button
              onClick={onClose}
              className="text-ui-text-tertiary hover:text-ui-text-primary transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-red-600 dark:text-red-400 text-sm">⚠️</span>
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          )}
          
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
            <p className="text-amber-800 dark:text-amber-200 text-sm">
              ⚠️ This will create a new pricing entry with today's effective date and end-date the current pricing. 
              This maintains pricing history while updating current rates.
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                  Provider
                </label>
                <input
                  type="text"
                  value={selectedPricing.provider}
                  disabled
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-tertiary dark:bg-neutral-600 text-ui-text-tertiary cursor-not-allowed"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                  Model
                </label>
                <input
                  type="text"
                  value={selectedPricing.model}
                  disabled
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-tertiary dark:bg-neutral-600 text-ui-text-tertiary cursor-not-allowed"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                  Input Price (per 1M tokens) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.input_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, input_price: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                  Output Price (per 1M tokens) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.output_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, output_price: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                  Cache Read Price (per 1M tokens)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cache_read_price || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    cache_read_price: e.target.value ? parseFloat(e.target.value) : undefined 
                  }))}
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  placeholder="Optional"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                  Cache Write Price (per 1M tokens)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cache_write_price || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    cache_write_price: e.target.value ? parseFloat(e.target.value) : undefined 
                  }))}
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  placeholder="Optional"
                />
              </div>
            </div>
            
            {/* Pricing Strategy */}
            <div>
              <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                Pricing Strategy
              </label>
              <select
                value={formData.pricing_strategy}
                onChange={(e) => {
                  const val = e.target.value as 'flat_per_mtok' | 'tiered_per_request';
                  setFormData(prev => ({ ...prev, pricing_strategy: val }));
                }}
                className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
              >
                <option value="flat_per_mtok">Flat (per MTok)</option>
                <option value="tiered_per_request">Tiered (per request)</option>
              </select>
            </div>

            {/* Tier Editor */}
            {formData.pricing_strategy === 'tiered_per_request' && (
              <div className="space-y-3">
                {tierValidationError && (
                  <div className="text-sm text-red-600 dark:text-red-400">{tierValidationError}</div>
                )}
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-ui-text-tertiary">
                        <th className="px-2 py-1">Min</th>
                        <th className="px-2 py-1">Max</th>
                        <th className="px-2 py-1">Input $/1M</th>
                        <th className="px-2 py-1">Output $/1M</th>
                        <th className="px-2 py-1">Cache R $/1M</th>
                        <th className="px-2 py-1">Cache W $/1M</th>
                        <th className="px-2 py-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(formData.tiers || []).map((t, i) => (
                        <tr key={i}>
                          <td className="px-2 py-1"><input type="number" min={0} value={t.min_input_tokens}
                            onChange={(e) => setFormData(prev => {
                              const tiers = [...(prev.tiers || [])];
                              tiers[i] = { ...tiers[i], min_input_tokens: Number(e.target.value) } as PricingTierForm;
                              tiers.sort((a, b) => a.min_input_tokens - b.min_input_tokens);
                              return { ...prev, tiers };
                            })}
                            className="w-24 px-2 py-1 border border-ui-border-primary rounded" /></td>
                          <td className="px-2 py-1"><input type="number" min={0} value={t.max_input_tokens ?? ''}
                            placeholder="∞"
                            onChange={(e) => setFormData(prev => {
                              const tiers = [...(prev.tiers || [])];
                              tiers[i] = { ...tiers[i], max_input_tokens: e.target.value === '' ? null : Number(e.target.value) } as PricingTierForm;
                              return { ...prev, tiers };
                            })}
                            className="w-24 px-2 py-1 border border-ui-border-primary rounded" /></td>
                          <td className="px-2 py-1"><input type="number" min={0} step="0.01" value={t.input_price}
                            onChange={(e) => setFormData(prev => {
                              const tiers = [...(prev.tiers || [])];
                              tiers[i] = { ...tiers[i], input_price: Number(e.target.value) } as PricingTierForm;
                              return { ...prev, tiers };
                            })}
                            className="w-28 px-2 py-1 border border-ui-border-primary rounded" /></td>
                          <td className="px-2 py-1"><input type="number" min={0} step="0.01" value={t.output_price}
                            onChange={(e) => setFormData(prev => {
                              const tiers = [...(prev.tiers || [])];
                              tiers[i] = { ...tiers[i], output_price: Number(e.target.value) } as PricingTierForm;
                              return { ...prev, tiers };
                            })}
                            className="w-28 px-2 py-1 border border-ui-border-primary rounded" /></td>
                          <td className="px-2 py-1"><input type="number" min={0} step="0.01" value={t.cache_read_price ?? ''}
                            onChange={(e) => setFormData(prev => {
                              const tiers = [...(prev.tiers || [])];
                              tiers[i] = { ...tiers[i], cache_read_price: e.target.value === '' ? undefined : Number(e.target.value) } as PricingTierForm;
                              return { ...prev, tiers };
                            })}
                            placeholder="Optional"
                            className="w-28 px-2 py-1 border border-ui-border-primary rounded" /></td>
                          <td className="px-2 py-1"><input type="number" min={0} step="0.01" value={t.cache_write_price ?? ''}
                            onChange={(e) => setFormData(prev => {
                              const tiers = [...(prev.tiers || [])];
                              tiers[i] = { ...tiers[i], cache_write_price: e.target.value === '' ? undefined : Number(e.target.value) } as PricingTierForm;
                              return { ...prev, tiers };
                            })}
                            placeholder="Optional"
                            className="w-28 px-2 py-1 border border-ui-border-primary rounded" /></td>
                          <td className="px-2 py-1">
                            <button onClick={() => setFormData(prev => ({ ...prev, tiers: (prev.tiers || []).filter((_, idx) => idx !== i) }))} className="p-1 text-red-600 hover:text-red-800"><Trash2 size={16} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button onClick={() => setFormData(prev => ({ ...prev, tiers: [...(prev.tiers || []), { min_input_tokens: 0, max_input_tokens: null, input_price: 0, output_price: 0 }] }))} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-ui-background-tertiary dark:bg-neutral-600 text-ui-text-secondary rounded hover:bg-ui-background-tertiary">
                  <Plus size={14} /> Add tier
                </button>
              </div>
            )}

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded border-ui-border-primary text-brand-primary focus:ring-brand-primary"
                />
                <span className="text-sm text-ui-text-secondary">Active</span>
              </label>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                rows={3}
                placeholder="Notes about this pricing update"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-ui-border-primary">
            <button
              onClick={onClose}
              className="px-4 py-2 text-ui-text-secondary border border-ui-border-primary rounded-lg hover:bg-ui-background-tertiary transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (formData.pricing_strategy === 'tiered_per_request') {
                  // simple reuse of validation logic
                  const sorted = [...(formData.tiers || [])].sort((a, b) => a.min_input_tokens - b.min_input_tokens);
                  let err: string | null = null;
                  if (sorted.length === 0) err = 'At least one tier is required for Tiered strategy';
                  else if (sorted[0].min_input_tokens !== 0) err = 'First tier must start at 0';
                  else {
                    for (let i = 0; i < sorted.length; i++) {
                      const t = sorted[i];
                      if (t.max_input_tokens != null && t.max_input_tokens <= t.min_input_tokens) { err = 'Tier max must be greater than min when provided'; break; }
                      if (i < sorted.length - 1) {
                        const next = sorted[i + 1];
                        if (t.max_input_tokens == null) { err = 'Only last tier can have open-ended max'; break; }
                        if (t.max_input_tokens !== next.min_input_tokens) { err = 'Tiers must be contiguous (prev.max == next.min)'; break; }
                      }
                    }
                  }
                  setTierValidationError(err);
                  if (err) return;
                }
                onSubmit();
              }}
              disabled={formData.input_price < 0 || formData.output_price < 0}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Update Pricing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PricingDetailsModal: React.FC<DetailsModalProps> = ({
  show,
  onClose,
  pricing
}) => {
  if (!show || !pricing) return null;

  const formatPrice = (price: string | undefined) => {
    if (price === undefined || price === null) return 'N/A';
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return 'N/A';
    return `$${numPrice.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-ui-text-primary">
              Pricing Details
            </h3>
            <button
              onClick={onClose}
              className="text-ui-text-tertiary hover:text-ui-text-primary transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="space-y-6">
            {/* Provider and Model */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-1">
                  Provider
                </label>
                <div className="flex items-center gap-2">
                  <Building size={16} className="text-gray-400" />
                  <span className="text-ui-text-primary font-medium">{pricing.provider}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-1">
                  Model
                </label>
                <div className="flex items-center gap-2">
                  <Cpu size={16} className="text-gray-400" />
                  <span className="text-ui-text-primary font-medium">{pricing.model}</span>
                </div>
              </div>
            </div>
            
            {/* Pricing */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-1">
                  Input Price (per 1M tokens)
                </label>
                <div className="flex items-center gap-2">
                  <DollarSign size={16} className="text-green-500" />
                  <span className="text-ui-text-primary font-medium">{formatPrice(pricing.input_price)}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-1">
                  Output Price (per 1M tokens)
                </label>
                <div className="flex items-center gap-2">
                  <DollarSign size={16} className="text-blue-500" />
                  <span className="text-ui-text-primary font-medium">{formatPrice(pricing.output_price)}</span>
                </div>
              </div>
            </div>
            
            {/* Cache Pricing */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-1">
                  Cache Read Price (per 1M tokens)
                </label>
                <div className="flex items-center gap-2">
                  <DollarSign size={16} className="text-purple-500" />
                  <span className="text-ui-text-primary font-medium">{formatPrice(pricing.cache_read_price)}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-1">
                  Cache Write Price (per 1M tokens)
                </label>
                <div className="flex items-center gap-2">
                  <DollarSign size={16} className="text-orange-500" />
                  <span className="text-ui-text-primary font-medium">{formatPrice(pricing.cache_write_price)}</span>
                </div>
              </div>
            </div>

            {/* Tier Table */}
            {pricing.tiers && pricing.tiers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-2">
                  Tiers (per request)
                </label>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left text-ui-text-tertiary">
                        <th className="px-2 py-1">Min</th>
                        <th className="px-2 py-1">Max</th>
                        <th className="px-2 py-1">Input $/1M</th>
                        <th className="px-2 py-1">Output $/1M</th>
                        <th className="px-2 py-1">Cache R</th>
                        <th className="px-2 py-1">Cache W</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pricing.tiers.map((t, i) => (
                        <tr key={i}>
                          <td className="px-2 py-1">{t.min_input_tokens}</td>
                          <td className="px-2 py-1">{t.max_input_tokens ?? '∞'}</td>
                          <td className="px-2 py-1">${parseFloat(t.input_price).toFixed(2)}</td>
                          <td className="px-2 py-1">${parseFloat(t.output_price).toFixed(2)}</td>
                          <td className="px-2 py-1">{t.cache_read_price ? `$${parseFloat(t.cache_read_price).toFixed(2)}` : '-'}</td>
                          <td className="px-2 py-1">{t.cache_write_price ? `$${parseFloat(t.cache_write_price).toFixed(2)}` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Status and Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-1">
                  Status
                </label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  pricing.is_active
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                }`}>
                  {pricing.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-1">
                  Effective Date
                </label>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-400" />
                  <span className="text-ui-text-primary">{formatDate(pricing.effective_date)}</span>
                </div>
              </div>
            </div>
            
            {pricing.end_date && (
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-1">
                  End Date
                </label>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-400" />
                  <span className="text-ui-text-primary">{formatDate(pricing.end_date)}</span>
                </div>
              </div>
            )}
            
            {/* Audit Information */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-1">
                  Created At
                </label>
                <span className="text-ui-text-primary text-sm">{formatDate(pricing.created_at)}</span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-1">
                  Updated At
                </label>
                <span className="text-ui-text-primary text-sm">{formatDate(pricing.updated_at)}</span>
              </div>
            </div>
            
            {/* Notes */}
            {pricing.notes && (
              <div>
                <label className="block text-sm font-medium text-ui-text-tertiary mb-1">
                  Notes
                </label>
                <div className="bg-ui-background-tertiary rounded-lg p-3">
                  <p className="text-ui-text-primary text-sm">{pricing.notes}</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end mt-6 pt-4 border-t border-ui-border-primary">
            <button
              onClick={onClose}
              className="px-4 py-2 text-ui-text-secondary border border-ui-border-primary rounded-lg hover:bg-ui-background-tertiary transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
